// MIGRATION NOTE: This context is tightly coupled to DataModelContext's hierarchyId tree
// (all drag operations identify items by hierarchyId and mutate the tree via useDataModelContext).
// It is NOT used by the layout editor (LayoutEditContext) or the structure editor (when built).
// When the structure editor adopts LayoutEditContext, drag-and-drop will need a new context
// that operates on Layout groups/modules rather than the hierarchyId tree — or this context
// must be refactored to support both data models. See TICKETS.md (HK-6) for the broader
// LayoutEditContext rename/generalization work that should happen alongside this.
import { createContext, useCallback, useRef, useState } from 'react';
import { DragDropContext, DropResult, OnDragEndResponder, OnDragStartResponder } from 'react-beautiful-dnd';
import cloneDeep from 'lodash/cloneDeep';
import { v4 as uuidv4 } from 'uuid';

import { useAlert } from 'contexts/alert/useAlert';
import type { ContentItem } from 'contexts/datamodel/DataModelContext';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { useTrash } from 'contexts/trash/useTrash';
import {
  ArticleAttributes,
  ArticleItem,
  CollectionDto,
  ExternalCollectionItem,
  ListContainer,
  ModuleContainer,
  NewsletterPageContainer
} from 'data/generated/graphql';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useScratchpadContext } from 'features/section-page-edit/components/scratch-pad/scratchpad-context';
import { AllessehContent } from 'hooks/useAllessehContentQuery';
import {
  addExcludedContentToQuery,
  getAllessehJsonQuery,
  removeExcludedContentFromQuery,
  setJsonQueryStr
} from 'utils/collection';
import { DROPPABLE_IDS } from 'utils/collectionDragUtils';
import { isItemAllowedInModule } from 'utils/modules';
import { safelyParseContent } from 'utils/temp';
import { DRAGGABLE_PREFIXES, DROPPABLE_ID_PREFIXES } from './dragUtils';

import { EntityType } from 'contexts/datamodel/DataModelContext.constants';

type SuffixOptions = {
  hierarchyId?: string | undefined;
  content?: AllessehContent | undefined;
  collection?: CollectionDto | undefined;
};

// GROUPS PATH — handler registration for groups-based drag events
export interface GroupsDragHandlers {
  onDragEnd: (result: DropResult) => void;
}
// END GROUPS PATH

interface DragAndDropContext {
  currentSourceDroppableId: string | null;
  generateDraggableId: (draggablePrefix: string, suffix: SuffixOptions) => string;
  searchInserted: boolean;
  isDraggingFromContentSearch: boolean;
  notifySearchInserted: () => void;
  acknowledgeSearchInsert: () => void;
  // GROUPS PATH
  registerGroupsDragHandlers: (handlers: GroupsDragHandlers) => void;
  unregisterGroupsDragHandlers: () => void;
  // END GROUPS PATH
}

const DEFAULT_DRAG_AND_DROP_CONTEXT: DragAndDropContext = {
  currentSourceDroppableId: null,
  generateDraggableId: () => '',
  searchInserted: false,
  isDraggingFromContentSearch: false,
  notifySearchInserted: () => {},
  acknowledgeSearchInsert: () => {},
  // GROUPS PATH
  registerGroupsDragHandlers: () => {},
  unregisterGroupsDragHandlers: () => {}
  // END GROUPS PATH
};

export const DragAndDropContext = createContext(DEFAULT_DRAG_AND_DROP_CONTEXT);

export const DragAndDropProvider: FCC = ({ children }) => {
  const { alertError } = useAlert();
  const {
    fromAllessehContent,
    generateHierarchyIdFromDragEvent,
    getEntity,
    insertEntity,
    swapEntities,
    removeEntity,
    isItemInDataModel,
    root,
    setNewRoot,
    modifyEntity,
    traverseEntity
  } = useDataModelContext();

  const scratchpadCtx = useScratchpadContext();

  const { setIsDragging } = useTrash();
  const [currentSourceDroppableId, setCurrentSourceDroppableId] = useState<string | null>(
    DEFAULT_DRAG_AND_DROP_CONTEXT.currentSourceDroppableId
  );
  const [isDraggingFromContentSearch, setIsDraggingFromContentSearch] = useState(false);

  // GROUPS PATH — ref-based registration so groups handlers can be called from here
  const groupsDragHandlersRef = useRef<GroupsDragHandlers | null>(null);
  const registerGroupsDragHandlers = useCallback((handlers: GroupsDragHandlers) => {
    groupsDragHandlersRef.current = handlers;
  }, []);
  const unregisterGroupsDragHandlers = useCallback(() => {
    groupsDragHandlersRef.current = null;
  }, []);
  // END GROUPS PATH

  // boolean flag: set true when something from Search/Results was inserted
  // SearchContent should call acknowledgeSearchInsert() after it clears the input
  const [searchInserted, setSearchInserted] = useState<boolean>(false);
  const notifySearchInserted = () => setSearchInserted(true);
  const acknowledgeSearchInsert = () => setSearchInserted(false);
  const handleDragStart: OnDragStartResponder = (dragEvent) => {
    // when an item currently in a module is being dragged
    // update the isDragging context so that the droppable trash area becomes active
    const regexDroppable = new RegExp(
      `^${DROPPABLE_ID_PREFIXES.MODULE_ITEMS}|${DROPPABLE_ID_PREFIXES.BANNER_MODULE_ITEMS}|${DROPPABLE_ID_PREFIXES.OFF_PLATFORM}|${DROPPABLE_ID_PREFIXES.COLLECTION_ITEMS}|${DROPPABLE_ID_PREFIXES.SCRATCHPAD}`
    );
    const regexDraggable = new RegExp(
      `^${DRAGGABLE_PREFIXES.MODULE_ITEM}|${DRAGGABLE_PREFIXES.COLLECTION_ITEM}|${DRAGGABLE_PREFIXES.SCRATCHPAD_ITEM}`
    );

    if (dragEvent.draggableId.match(regexDraggable) && dragEvent.source.droppableId.match(regexDroppable)) {
      setIsDragging(true);
    }

    if (dragEvent.source.droppableId.startsWith(DROPPABLE_IDS.RESULTS)) {
      setIsDraggingFromContentSearch(true);
    }

    setCurrentSourceDroppableId(dragEvent.source.droppableId);
  };

  const handleReinclude = (sourceIndex: number) => {
    if (root.type === EntityType.List) {
      const rootListContainer = root as ListContainer;
      const articleAttribute = rootListContainer.collection[sourceIndex].attributes as ArticleAttributes;
      const allessehContentId = articleAttribute.id;

      const jsonQueryStr = getAllessehJsonQuery(rootListContainer);
      const queryItemIndex = rootListContainer.collection.findIndex((item) => item.type === EntityType.Query);

      if (jsonQueryStr) {
        const parsedJsonQuery = removeExcludedContentFromQuery(allessehContentId, jsonQueryStr);
        setJsonQueryStr(JSON.stringify(parsedJsonQuery), queryItemIndex, modifyEntity);
      }
    }
  };

  const handleExclude = (allessehContentId: string) => {
    if (root.type === EntityType.List) {
      const rootListContainer = root as ListContainer;
      const jsonQueryStr = getAllessehJsonQuery(rootListContainer);
      const queryItemIndex = rootListContainer.collection.findIndex((item) => item.type === EntityType.Query);

      if (jsonQueryStr) {
        const parsedJsonQuery = addExcludedContentToQuery(allessehContentId, jsonQueryStr);
        setJsonQueryStr(JSON.stringify(parsedJsonQuery), queryItemIndex, modifyEntity);
      }
    }
  };

  const handleDragEnd: OnDragEndResponder = (dragEvent) => {
    setIsDragging(false);
    setIsDraggingFromContentSearch(false);
    setCurrentSourceDroppableId(null);

    let isDuplicateFromHistory: boolean = false;

    if (dragEvent.draggableId.startsWith(DRAGGABLE_PREFIXES.HISTORY_MODULE_ITEM)) {
      const draggedEntityStr = dragEvent.draggableId.replace(DRAGGABLE_PREFIXES.HISTORY_MODULE_ITEM, '');
      const draggableEntity = safelyParseContent<ArticleItem>(draggedEntityStr);

      traverseEntity(root, (entity) => {
        if (
          entity.type === EntityType.Article &&
          draggableEntity.attributes.id === (entity as ArticleItem).attributes.id
        ) {
          isDuplicateFromHistory = true;
        }
      });
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (isDuplicateFromHistory) {
      return;
    }

    if (!dragEvent.destination) {
      return;
    }

    const {
      destination: { droppableId: destDroppableId, index: destIndex },
      source: { droppableId: sourceDroppableId, index: sourceIndex },
      draggableId
    } = dragEvent;
    const isModuleDrop = destDroppableId.startsWith(DROPPABLE_ID_PREFIXES.MODULES);
    const isGroupDrop = destDroppableId.startsWith(DROPPABLE_ID_PREFIXES.GROUP);

    if (isModuleDrop || isGroupDrop) {
      swapEntities(
        generateHierarchyIdFromDragEvent(sourceDroppableId, sourceIndex),
        generateHierarchyIdFromDragEvent(destDroppableId, destIndex)
      );
    } else if (destDroppableId.startsWith(DROPPABLE_ID_PREFIXES.MODULE_ITEMS)) {
      const destModule = getEntity<ModuleContainer>(destDroppableId)!;

      // Adding from the results page into a module
      if (sourceDroppableId.startsWith(DROPPABLE_IDS.RESULTS)) {
        const item = safelyParseContent<ContentItem>(draggableId.replace(/.+?#/, ''));

        if (!isItemAllowedInModule(destModule, item, alertError)) return;

        insertEntity(generateHierarchyIdFromDragEvent(destDroppableId, destIndex), item);
        notifySearchInserted();
      } else if (sourceDroppableId.startsWith(DROPPABLE_ID_PREFIXES.SCRATCHPAD)) {
        // Adding from the scratchpad into a module
        const item = safelyParseContent<ContentItem>(draggableId.replace(/.+?#/, ''));

        if (!isItemAllowedInModule(destModule, item, alertError)) return;

        const didAdd = insertEntity(generateHierarchyIdFromDragEvent(destDroppableId, destIndex), item);

        if (didAdd) {
          scratchpadCtx?.removeItemFromScratchpad(item as ArticleItem);
        }
      } else if (sourceDroppableId.startsWith(DROPPABLE_ID_PREFIXES.COLLECTION)) {
        const item = safelyParseContent<CollectionDto>(draggableId.replace(/.+?#/, ''));
        const externalCollectionItem: ExternalCollectionItem = {
          type: EntityType.Collection,
          uuid: uuidv4(),
          attributes: {
            id: item.metadata.allessehId,
            base_doc_id: item.metadata.allessehId,
            repo: 'Allesseh',
            limit: 5
          },
          metadata: {
            id: item.metadata.allessehId,
            name: item.metadata.name,
            createdUtc: item.metadata.createdUtc,
            creatorUser: item.metadata.creatorUser,
            isArchived: item.metadata.isArchived,
            notes: item.metadata.notes ?? '',
            publicationKey: item.metadata.publicationKey,
            revisorUser: item.metadata.revisorUser ?? { djUserId: '' },
            updatedUtc: item.metadata.updatedUtc ?? 0
          },
          contentItems: item.root.collection
        };

        if (!isItemAllowedInModule(destModule, externalCollectionItem, alertError)) return;

        insertEntity(generateHierarchyIdFromDragEvent(destDroppableId, destIndex), externalCollectionItem);
      } else if (sourceDroppableId.startsWith(DROPPABLE_ID_PREFIXES.MODULE_ITEMS)) {
        const sourceItemHierarchyId = generateHierarchyIdFromDragEvent(sourceDroppableId, sourceIndex);
        const sourceItem = getEntity<ArticleItem>(sourceItemHierarchyId)!;

        if (!isItemAllowedInModule(destModule, sourceItem, alertError)) {
          return;
        }

        swapEntities(sourceItemHierarchyId, generateHierarchyIdFromDragEvent(destDroppableId, destIndex));
      } else if (sourceDroppableId.startsWith(DROPPABLE_ID_PREFIXES.HISTORY)) {
        const item = safelyParseContent<ContentItem>(draggableId.replace(/.+?#/, ''));

        if (!isItemAllowedInModule(destModule, item, alertError)) return;

        insertEntity(generateHierarchyIdFromDragEvent(destDroppableId, destIndex), item);
      }
    } else if (destDroppableId.startsWith(DROPPABLE_ID_PREFIXES.COLLECTION_ITEMS)) {
      if (sourceDroppableId.startsWith(DROPPABLE_IDS.RESULTS)) {
        const item = safelyParseContent<ContentItem>(draggableId.replace(/.+?#/, ''));
        item.uuid = uuidv4();
        const articleAttributes = item.attributes as ArticleAttributes;
        handleExclude(articleAttributes.id);
        insertEntity(generateHierarchyIdFromDragEvent(destDroppableId, destIndex), item);
        notifySearchInserted();
      } else if (sourceDroppableId.startsWith(DROPPABLE_ID_PREFIXES.COLLECTION_ITEMS)) {
        swapEntities(
          generateHierarchyIdFromDragEvent(sourceDroppableId, sourceIndex),
          generateHierarchyIdFromDragEvent(destDroppableId, destIndex)
        );
      } else if (sourceDroppableId.startsWith(DROPPABLE_ID_PREFIXES.HISTORY)) {
        const item = safelyParseContent<ContentItem>(draggableId.replace(/.+?#/, ''));
        insertEntity(generateHierarchyIdFromDragEvent(destDroppableId, destIndex), item);
      }
    } else if (destDroppableId.startsWith(DROPPABLE_ID_PREFIXES.OFF_PLATFORM)) {
      if (sourceDroppableId.startsWith(DROPPABLE_IDS.RESULTS)) {
        const item = safelyParseContent<ContentItem>(draggableId.replace(/.+?#/, ''));

        insertEntity(generateHierarchyIdFromDragEvent(destDroppableId, destIndex), item);
        notifySearchInserted();
      }
      if (sourceDroppableId.startsWith(DROPPABLE_ID_PREFIXES.OFF_PLATFORM)) {
        const sourceItemHierarchyId = generateHierarchyIdFromDragEvent(sourceDroppableId, sourceIndex);
        swapEntities(sourceItemHierarchyId, generateHierarchyIdFromDragEvent(destDroppableId, destIndex));
      }

      if (sourceDroppableId.startsWith(DROPPABLE_ID_PREFIXES.HISTORY)) {
        const item = safelyParseContent<ContentItem>(draggableId.replace(/.+?#/, ''));

        if (!isItemInDataModel(item)) {
          insertEntity(generateHierarchyIdFromDragEvent(destDroppableId, destIndex), item);
        } else {
          alertError('The content was not added to the collection. It already exists in this collection.');
        }
      }
    } else if (destDroppableId.startsWith(DROPPABLE_ID_PREFIXES.NEWSLETTER)) {
      if (sourceDroppableId.startsWith(DROPPABLE_ID_PREFIXES.NEWSLETTER)) {
        const newsletter = cloneDeep(root) as NewsletterPageContainer;
        const sourceItem = newsletter.collection[sourceIndex];
        const destItem = newsletter.collection[destIndex];

        newsletter.collection[sourceIndex] = destItem;
        newsletter.collection[destIndex] = sourceItem;

        setNewRoot(newsletter);
      }
    } else if (destDroppableId.startsWith(DROPPABLE_ID_PREFIXES.BANNER_MODULE_ITEMS)) {
      const item = safelyParseContent<ContentItem>(draggableId.replace(/.+?#/, ''));

      if (sourceDroppableId.startsWith(DROPPABLE_IDS.RESULTS)) {
        // Remove the only article in the banner module, and insert the new one.
        // Effectively allowing only one article to be added to it.
        removeEntity(generateHierarchyIdFromDragEvent(destDroppableId, 0));
        insertEntity(generateHierarchyIdFromDragEvent(destDroppableId, 0), item);
        notifySearchInserted();
      }
    } else if (destDroppableId.startsWith(DROPPABLE_IDS.TRASH)) {
      if (!sourceDroppableId.startsWith(DROPPABLE_IDS.RESULTS)) {
        if (sourceDroppableId.startsWith(DROPPABLE_ID_PREFIXES.COLLECTION_ITEMS)) {
          handleReinclude(dragEvent.source.index);
        }

        // Moving item from scratchpad to trash
        if (sourceDroppableId.startsWith(DROPPABLE_ID_PREFIXES.SCRATCHPAD)) {
          const item = safelyParseContent<ContentItem>(draggableId.replace(/.+?#/, ''));
          scratchpadCtx?.removeItemFromScratchpad(item as ArticleItem);
          return;
        }

        removeEntity(generateHierarchyIdFromDragEvent(sourceDroppableId, sourceIndex));
      }
    } else if (destDroppableId.startsWith(DROPPABLE_ID_PREFIXES.SCRATCHPAD)) {
      // Adding from the results page into the scratchpad
      if (sourceDroppableId.startsWith(DROPPABLE_IDS.RESULTS)) {
        const item = safelyParseContent<ContentItem>(draggableId.replace(/.+?#/, ''));

        scratchpadCtx?.addItemToScratchpad(
          generateHierarchyIdFromDragEvent(destDroppableId, destIndex),
          item as ArticleItem
        );
        notifySearchInserted();
      }

      // Adding from a module into the scratchpad
      if (sourceDroppableId.startsWith(DROPPABLE_ID_PREFIXES.MODULE_ITEMS)) {
        const sourceItemHierarchyId = generateHierarchyIdFromDragEvent(sourceDroppableId, sourceIndex);

        const item = getEntity(sourceItemHierarchyId);

        if (!item) {
          return;
        }

        if (item.type === EntityType.Collection || item.type === EntityType.Query) {
          return;
        }

        scratchpadCtx?.addItemToScratchpadFromDraft(
          sourceItemHierarchyId,
          generateHierarchyIdFromDragEvent(destDroppableId, destIndex)
        );
      }

      // Reordering items in the scratchpad
      if (sourceDroppableId.startsWith(DROPPABLE_ID_PREFIXES.SCRATCHPAD)) {
        scratchpadCtx?.swapItemsInScratchpad(sourceIndex, destIndex);
      }
    }
    // GROUPS PATH — delegate to registered groups handler for module reorder and content drops
    else if (
      destDroppableId.startsWith('group-') ||
      destDroppableId.startsWith(DROPPABLE_ID_PREFIXES.PAGE_GROUP_MODULE_ITEMS)
    ) {
      if (
        destDroppableId.startsWith(DROPPABLE_ID_PREFIXES.PAGE_GROUP_MODULE_ITEMS) &&
        sourceDroppableId.startsWith(DROPPABLE_IDS.RESULTS)
      ) {
        notifySearchInserted();
      }
      groupsDragHandlersRef.current?.onDragEnd(dragEvent);
    }
    // END GROUPS PATH
  };

  const generateDraggableId = useCallback(
    (draggablePrefix: string, { hierarchyId, content, collection }: SuffixOptions) => {
      if (hierarchyId) {
        return draggablePrefix + hierarchyId;
      }

      if (content) {
        const articleItem = fromAllessehContent(content);
        return draggablePrefix + JSON.stringify(articleItem);
      }

      if (collection) {
        return draggablePrefix + JSON.stringify(collection);
      }

      throw new Error('Either a hierarchy ID or content or collection must be provided to generate a draggable ID');
    },
    [fromAllessehContent]
  );

  const value = {
    generateDraggableId,
    currentSourceDroppableId,
    searchInserted,
    notifySearchInserted,
    acknowledgeSearchInsert,
    isDraggingFromContentSearch,
    // GROUPS PATH
    registerGroupsDragHandlers,
    unregisterGroupsDragHandlers
    // END GROUPS PATH
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
      <DragAndDropContext.Provider value={value}>{children}</DragAndDropContext.Provider>
    </DragDropContext>
  );
};
